import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard, currency } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, AlertTriangle, CalendarClock, CheckCircle2, Sparkles, Upload, Loader2, User, ExternalLink, UserPlus } from "lucide-react";
import { AgentContact } from "@/components/AgentContact";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { inviteOutdated } from "@/components/CalendarInviteDialog";

const INVITE_DATE_LABELS = { contract_date: "Contract", earnest_money_deadline: "EM Due", closing_date: "Closing", inspection_deadline: "Inspection",
  financing_deadline: "Financing", appraisal_deadline: "Appraisal", insurance_deadline: "Insurance", possession_date: "Possession" };
const outdatedInvites = (t) => Object.keys(INVITE_DATE_LABELS).filter((k) => inviteOutdated(t, k)).map((k) => INVITE_DATE_LABELS[k]);
const OPEN_STATUSES = ["pre_listing", "active", "under_contract"];

const STATUS_TONE = {
  pre_listing: "bg-[#E7EEF5] text-[#1F3C88]",
  active: "bg-[#E7F0EA] text-[#2E5C47]",
  under_contract: "bg-[#FDF0E1] text-[#B4690E]",
  closed: "bg-[#1F3C88] text-white",
  cancelled: "bg-[#F6E4E4] text-[#992B2B]",
};
const inp = "w-full px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:ring-2 focus:ring-[#F7941D] focus:border-transparent focus:outline-none";
const lbl = "text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755] block mb-1";
const TASK_BUCKET_LABELS = { overdue: "Overdue Tasks", due_today: "Tasks Due Today", upcoming: "Upcoming Tasks (Next 48 Hours)" };
const daysTo = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;
const fmtMDY = (d) => {
  const s = (d || "").slice(0, 10);
  const [y, m, day] = s.split("-");
  return (y && m && day) ? `${m}/${day}/${y.slice(2)}` : "";
};

const INITIAL = {
  property_address: "", property_type: "single_family", transaction_side: "buyer", status: "active",
  contract_date: "", closing_date: "", earnest_money_deadline: "", inspection_deadline: "",
  appraisal_deadline: "", financing_deadline: "", purchase_price: "", earnest_money: "",
  buyer_name: "", buyer_email: "", buyer_phone: "",
  buyer2_name: "", buyer2_email: "", buyer2_phone: "",
  seller_name: "", seller_email: "", seller_phone: "",
  seller2_name: "", seller2_email: "", seller2_phone: "",
  buyer_agent_name: "", seller_agent_name: "",
  lender_name: "", lender_email: "", lender_phone: "",
  title_company_name: "", title_company_email: "", title_company_phone: "",
  notes: "", workflow_template_id: "", expected_close_days: "",
  coordinator_name: "", coordinator_email: "",
  city: "", state: "", zip: "",
};

export default function TransactionManager() {
  const { user } = useAuth();
  const canWrite = (user.tools || []).includes("transactions") || user.role === "admin";
  const navigate = useNavigate();
  const [txns, setTxns] = useState([]);
  const [dash, setDash] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [parsing, setParsing] = useState(false);
  const [contractFile, setContractFile] = useState(null);
  const [viewBucket, setViewBucket] = useState(null);
  const [popupCoord, setPopupCoord] = useState("__all__");
  useEffect(() => { setPopupCoord("__all__"); }, [viewBucket]);
  const freshForm = useCallback(() => ({ ...INITIAL, coordinator_name: user?.name || "", coordinator_email: user?.email || "" }), [user]);

  const load = useCallback(async () => {
    const [t, d] = await Promise.all([api.get("/tm/transactions"), api.get("/tm/dashboard")]);
    setTxns(t.data); setDash(d.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/tm/workflows").then((r) => setWorkflows(r.data)).catch(() => {}); }, []);
  const [sortKey, setSortKey] = useState("closing_date");
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("under_contract");
  const [coordFilter, setCoordFilter] = useState("__all__");
  const coordinators = Array.from(new Set(txns.map((t) => (t.coordinator_name || "").trim()).filter(Boolean))).sort();
  const hasUnassigned = txns.some((t) => !(t.coordinator_name || "").trim());
  const STATUS_BUCKETS = {
    pre_listing: (s) => s === "pre_listing",
    under_contract: (s) => s !== "closed" && s !== "cancelled" && s !== "pre_listing",
    closed: (s) => s === "closed",
    cancelled: (s) => s === "cancelled",
  };
  const clientName = (t) => t.transaction_side === "seller" ? (t.seller_name || "")
    : t.transaction_side === "buyer" ? (t.buyer_name || "")
    : (t.buyer_name || t.seller_name || "");
  const agentName = (t) => t.transaction_side === "seller" ? (t.seller_agent_name || t.buyer_agent_name || "")
    : (t.buyer_agent_name || t.seller_agent_name || "");
  const sortVal = (t) => {
    switch (sortKey) {
      case "property_address": return (t.property_address || "").toLowerCase();
      case "agent": return agentName(t).toLowerCase();
      case "transaction_side": return (t.transaction_side || "").toLowerCase();
      case "client": return clientName(t).toLowerCase();
      case "closing_date": return t.closing_date || "";
      case "tasks": return (t.tasks_total || 0) - (t.tasks_done || 0);
      case "status": return (t.status || "").toLowerCase();
      default: return "";
    }
  };
  const toggleSort = (key) => { if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(key); setSortDir("asc"); } };
  const arrow = (key) => (sortKey === key ? (sortDir === "asc" ? " ↑" : " ↓") : "");
  const q = search.trim().toLowerCase();
  const sortedTxns = [...txns]
    .filter((t) => (STATUS_BUCKETS[statusFilter] || (() => true))((t.status || "").toLowerCase()))
    .filter((t) => coordFilter === "__all__" || (coordFilter === "__unassigned__" ? !(t.coordinator_name || "").trim() : (t.coordinator_name || "").trim() === coordFilter))
    .filter((t) => !q || (t.property_address || "").toLowerCase().includes(q) || clientName(t).toLowerCase().includes(q))
    .sort((a, b) => {
      const av = sortVal(a), bv = sortVal(b);
      let cmp;
      if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else {
        const as = String(av), bs = String(bv);
        cmp = (!as && !bs) ? 0 : !as ? 1 : !bs ? -1 : as.localeCompare(bs);
      }
      return sortDir === "asc" ? cmp : -cmp;
    });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, purchase_price: parseFloat(form.purchase_price) || null, earnest_money: parseFloat(form.earnest_money) || null };
    if (contractFile) payload.documents = [{ ...contractFile, kind: "contract", uploaded_at: new Date().toISOString() }];
    try {
      await api.post("/tm/transactions", payload);
      toast.success("Transaction created");
      setOpen(false); setForm(freshForm()); setContractFile(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const parseContract = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setParsing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/tm/parse-contract", fd);
      const filled = data.filled || [];
      if (data.file) setContractFile(data.file);
      if (filled.length === 0) {
        toast.warning("Couldn't read any fields from that file. Please enter the details manually.");
      } else {
        setForm((prev) => ({ ...prev, ...data.fields }));
        toast.success(`Auto-filled ${filled.length} field${filled.length === 1 ? "" : "s"} from the contract — review before saving.`);
        if (data.warning) toast.warning(data.warning);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setParsing(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/tm/transactions/export-csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "transactions.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/tm/transactions/import-csv", fd);
      toast.success(`Imported ${res.data.created} transaction(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div data-testid="transactions-page">
      <PageHeader
        title="Transaction Manager"
        subtitle="EasyOffice contract-to-close: key dates, workflow checklists and automatic deadline tracking for every deal."
        action={canWrite && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button data-testid="tm-export-csv" onClick={exportCsv} className="inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors">Export</button>
            <label data-testid="tm-import-csv" className="inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors cursor-pointer">Import<input type="file" accept=".csv" hidden onChange={importCsv} /></label>
            <button data-testid="tm-reports-btn" onClick={() => navigate("/transactions/reports")} className="inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors">
              Reports
            </button>
            <button data-testid="manage-workflows-btn" onClick={() => navigate("/transactions/workflows")} className="inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors">
              Workflows
            </button>
            <button data-testid="add-person-button" onClick={() => navigate("/people")} className="inline-flex items-center justify-center px-4 py-2.5 rounded-full border border-[#E5E5E2] text-sm font-semibold text-[#1F3C88] hover:bg-[#F0EFEC] transition-colors">
              People
            </button>
            <button data-testid="add-transaction-button" onClick={() => { setForm(freshForm()); setContractFile(null); setOpen(true); }} className="inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-[#F7941D] text-white text-sm font-semibold hover:bg-[#E08312] transition-colors duration-200">
              New Transaction
            </button>
          </div>
        )}
      />

      {dash?.stalling?.length > 0 && (
        <div data-testid="stalling-banner" className="mb-8 bg-[#FDF3F3] border border-[#E9C9C9] rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-[#EBD3D3] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#992B2B]" />
            <span className="text-sm font-semibold text-[#992B2B]">{dash.stalling.length} deal{dash.stalling.length === 1 ? "" : "s"} need attention</span>
          </div>
          <div className="divide-y divide-[#F0E0E0]">
            {dash.stalling.slice(0, 5).map((s) => (
              <button key={s.transaction_id} data-testid={`stalling-${s.transaction_id}`} onClick={() => navigate(`/transactions/${s.transaction_id}`)}
                className="w-full text-left px-5 py-2.5 flex items-center justify-between gap-4 hover:bg-[#FBEDED] transition-colors duration-200">
                <span className="text-sm font-medium text-[#0A0A0A] truncate">{s.property_address}</span>
                <span className="text-xs text-[#992B2B] shrink-0">{s.reason}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {(() => {
        const pending = txns.filter((t) => OPEN_STATUSES.includes(t.status)).map((t) => ({ t, dates: outdatedInvites(t) })).filter((x) => x.dates.length);
        if (!pending.length) return null;
        return (
          <div data-testid="invite-resend-board" className="mb-8 bg-[#FFF8EE] border border-[#F7D9A8] rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-[#F7E3C2] flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-[#9a5b12]" />
              <span className="text-sm font-semibold text-[#9a5b12]" data-testid="invite-resend-count">{pending.length} file{pending.length === 1 ? "" : "s"} with changed dates waiting for a calendar resend</span>
            </div>
            <div className="divide-y divide-[#F7E9D2]">
              {pending.map(({ t, dates }) => (
                <button key={t.transaction_id} data-testid={`invite-resend-${t.transaction_id}`} onClick={() => navigate(`/transactions/${t.transaction_id}`)}
                  className="w-full text-left px-5 py-2.5 flex items-center justify-between gap-4 hover:bg-[#FDF0DC] transition-colors duration-200">
                  <span className="text-sm font-medium text-[#0A0A0A] truncate">{t.property_address}<span className="ml-2 text-xs font-normal text-[#8a8a86]">{t.coordinator_name || ""}</span></span>
                  <span className="text-xs text-[#9a5b12] shrink-0">Resend: {dates.join(", ")}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })()}

      {dash && (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-10">
          <AddressTile index={0} label="Pre-Listing" value={dash.pre_listing_count || 0} sub="Pre-listing files" items={dash.pre_listing_list} nav={navigate} />
          <AddressTile index={1} label="Active" value={dash.active_count} sub={`${dash.total} total`} items={dash.active_list} nav={navigate} />
          <AddressTile index={2} label="Closing ≤14 days" value={dash.closing_soon} sub="Upcoming closings" items={dash.closing_soon_list} nav={navigate} />
          <TaskTile index={3} label="Tasks Overdue" bucket="overdue" tone="text-[#992B2B]" items={dash.overdue} sub="No overdue tasks" nav={navigate} onViewAll={setViewBucket} />
          <TaskTile index={4} label="Due Today" bucket="due_today" tone="text-[#B07D14]" items={dash.due_today} sub="Nothing due today" nav={navigate} onViewAll={setViewBucket} />
          <TaskTile index={5} label="Upcoming Tasks" bucket="upcoming" tone="text-[#2E5C47]" items={dash.upcoming} sub="Nothing upcoming" nav={navigate} onViewAll={setViewBucket} />
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white border border-[#E5E5E2] rounded-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E5E5E2] flex items-center justify-between gap-3">
            <h3 className="font-serif text-xl">Transactions</h3>
            <div className="flex items-center gap-2">
              <select data-testid="txn-coordinator-filter" value={coordFilter} onChange={(e) => setCoordFilter(e.target.value)}
                className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
                <option value="__all__">All Coordinators</option>
                {hasUnassigned && <option value="__unassigned__">Unassigned</option>}
                {coordinators.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="txn-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
                <option value="pre_listing">Pre Listing</option>
                <option value="under_contract">Under Contract</option>
                <option value="closed">Closed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <input data-testid="txn-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client or property…"
                className="px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm w-48 max-w-[40%] focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E5E5E2] text-left text-xs tracking-[0.12em] uppercase text-[#575755]">
                {[["property_address", "Property"], ["agent", "Agent"], ["transaction_side", "Side"], ["client", "Client"], ["closing_date", "Closing"], ["tasks", "Tasks"], ["status", "Status"]].map(([key, label]) => (
                  <th key={key} data-testid={`sort-${key}`} onClick={() => toggleSort(key)} className="px-4 py-3 font-semibold cursor-pointer select-none hover:text-[#1F3C88] transition-colors whitespace-nowrap">{label}{arrow(key)}</th>
                ))}
              </tr></thead>
              <tbody>
                {sortedTxns.map((t) => {
                  const dt = daysTo(t.closing_date);
                  const client = t.transaction_side === "seller" ? (t.seller_name || "—")
                    : t.transaction_side === "buyer" ? (t.buyer_name || "—")
                    : (t.buyer_name || t.seller_name || "—");
                  return (
                    <tr key={t.transaction_id} data-testid={`txn-row-${t.transaction_id}`} onClick={() => navigate(`/transactions/${t.transaction_id}`)}
                      className="border-b border-[#F0EFEC] hover:bg-[#FBFAF8] cursor-pointer transition-colors duration-200">
                      <td className="px-4 py-3"><div className="font-medium text-[#0A0A0A]">{t.property_address}</div>
                        {outdatedInvites(t).length > 0 && <span data-testid={`txn-invite-flag-${t.transaction_id}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[#F7D9A8] text-[#9a5b12] bg-[#FFF4E5]"><CalendarClock className="w-3 h-3" /> Resend invite: {outdatedInvites(t).join(", ")}</span>}</td>
                      <td className="px-4 py-3">{(t.transaction_side === "seller" ? t.seller_agent_profile : t.buyer_agent_profile) || t.buyer_agent_profile || t.seller_agent_profile || t.buyer_agent_name || t.seller_agent_name
                        ? <AgentContact profile={t.transaction_side === "seller" ? (t.seller_agent_profile || t.buyer_agent_profile) : (t.buyer_agent_profile || t.seller_agent_profile)} name={t.transaction_side === "seller" ? (t.seller_agent_name || t.buyer_agent_name) : (t.buyer_agent_name || t.seller_agent_name)} />
                        : <span className="text-xs text-[#a3a099]">—</span>}</td>
                      <td className="px-4 py-3 text-[#575755] capitalize">{t.transaction_side}</td>
                      <td className="px-4 py-3 text-[#0A0A0A]" data-testid={`txn-client-${t.transaction_id}`}>{client}</td>
                      <td className="px-4 py-3 text-[#575755]">{fmtMDY(t.closing_date) || "—"}{dt != null && dt >= 0 && dt <= 14 && <span className="ml-1 text-xs text-[#F7941D]">({dt}d)</span>}</td>
                      <td className="px-4 py-3"><span className="text-xs tabular-nums">{t.tasks_done}/{t.tasks_total}</span>
                        <div className="w-20 h-1.5 bg-[#F0EFEC] rounded-full mt-1 overflow-hidden"><div className="h-full bg-[#2E5C47]" style={{ width: `${t.tasks_total ? (t.tasks_done / t.tasks_total) * 100 : 0}%` }} /></div>
                      </td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${STATUS_TONE[t.status] || STATUS_TONE.active}`}>{(t.status || "").replace("_", " ")}</span></td>
                    </tr>
                  );
                })}
                {sortedTxns.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#8a8a86]">{q ? "No transactions match your search." : "No transactions yet."}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(freshForm()); setContractFile(null); } }}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">New Transaction</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-5" data-testid="transaction-form">
            <label data-testid="contract-upload-dropzone" className={`flex items-center gap-3 p-4 rounded-sm border-2 border-dashed transition-colors duration-200 ${parsing ? "border-[#F7941D] bg-[#FFF7EE] cursor-wait" : "border-[#C9C9C6] hover:border-[#F7941D] hover:bg-[#FBFAF8] cursor-pointer"}`}>
              <div className="w-10 h-10 rounded-full bg-[#1F3C88] flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={1.75} />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-[#1F3C88]">{parsing ? "Reading contract…" : "Auto-fill from contract"}</div>
                <div className="text-xs text-[#575755]">{parsing ? "Your assistant is pulling key dates, parties and price." : "Upload a PDF or photo of the contract and AI fills the form for you to review."}</div>
              </div>
              {parsing ? <Loader2 className="w-5 h-5 text-[#F7941D] animate-spin" /> : <Upload className="w-5 h-5 text-[#8a8a86]" />}
              <input data-testid="contract-upload-input" type="file" accept=".pdf,image/*" hidden disabled={parsing} onChange={parseContract} />
            </label>
            {contractFile && (
              <div data-testid="contract-attached-chip" className="flex items-center gap-2 -mt-2 text-xs text-[#2E5C47]">
                <CheckCircle2 className="w-3.5 h-3.5" /> {contractFile.name} will be attached to this transaction
              </div>
            )}
            <div><span className={lbl}>Property Address</span>
              <AddressAutocomplete required testid="txn-address-input" className={inp} value={form.property_address}
                onChange={(v) => setForm((f) => ({ ...f, property_address: v }))}
                onSelect={(s) => setForm((f) => ({ ...f, property_address: s.label, city: s.city || f.city, state: s.state || f.state, zip: s.postcode || f.zip }))} />
            </div>
            <div><span className={lbl}>Coordinator</span>
              <input data-testid="txn-coordinator-input" className={inp} value={form.coordinator_name}
                onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, coordinator_name: v, coordinator_email: v.trim().toLowerCase() === (user?.name || "").trim().toLowerCase() ? (user?.email || "") : "" })); }} />
              <p className="text-[11px] text-[#8a8a86] mt-1">Defaults to you — change it if another coordinator owns this file.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={lbl}>Status</span>
                <select data-testid="txn-status-input" className={inp} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {["pre_listing", "active", "under_contract", "closed", "cancelled"].map((x) => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
                </select>
              </div>
              <div><span className={lbl}>Type</span>
                <select className={inp} value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}>
                  {["single_family", "condo", "townhouse", "multi_family", "land"].map((x) => <option key={x} value={x}>{x.replace("_", " ")}</option>)}
                </select>
              </div>
              <div><span className={lbl}>Side</span>
                <select className={inp} value={form.transaction_side} onChange={(e) => setForm({ ...form, transaction_side: e.target.value })}>
                  {["buyer", "seller", "dual"].map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div><span className={lbl}>Workflow</span>
                <select data-testid="workflow-select" className={inp} value={form.workflow_template_id} onChange={(e) => setForm({ ...form, workflow_template_id: e.target.value })}>
                  <option value="">None</option>
                  {workflows.map((w) => <option key={w.template_id} value={w.template_id}>{w.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#F7941D]">Key Dates</span>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[["contract_date", "Contract"], ["closing_date", "Closing"], ["earnest_money_deadline", "Earnest $"], ["inspection_deadline", "Inspection"], ["appraisal_deadline", "Appraisal"], ["financing_deadline", "Financing"]].map(([k, label]) => (
                  <div key={k}><span className={lbl}>{label}</span><input type="date" data-testid={`date-${k}`} className={inp} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={lbl}>Purchase Price</span><input type="number" className={inp} value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} /></div>
              <div><span className={lbl}>Earnest Money</span><input type="number" className={inp} value={form.earnest_money} onChange={(e) => setForm({ ...form, earnest_money: e.target.value })} /></div>
            </div>
            <div><span className={lbl}>Expected Days to Close</span><input type="number" min="1" data-testid="field-expected_close_days" className={inp} placeholder="30 (default)" value={form.expected_close_days} onChange={(e) => setForm({ ...form, expected_close_days: e.target.value })} /></div>
            <div>
              <span className="text-xs tracking-[0.15em] uppercase font-semibold text-[#F7941D]">Parties</span>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {[["buyer_agent_name", "Buyer Agent"], ["seller_agent_name", "Seller Agent"]].map(([k, label]) => (
                  <div key={k}><span className={lbl}>{label}</span><input data-testid={`party-${k}`} className={inp} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
                ))}
              </div>
              {[["buyer", "Buyer"], ["buyer2", "Buyer 2"], ["seller", "Seller"], ["seller2", "Seller 2"], ["lender", "Lender"], ["title_company", "Title Company"]].map(([p, label]) => (
                <div key={p} className="mt-3">
                  <span className={lbl}>{label}</span>
                  <div className="grid grid-cols-3 gap-3">
                    <input data-testid={`party-${p}_name`} className={inp} placeholder="Name" value={form[`${p}_name`]} onChange={(e) => setForm({ ...form, [`${p}_name`]: e.target.value })} />
                    <input data-testid={`party-${p}_email`} type="email" className={inp} placeholder="Email" value={form[`${p}_email`]} onChange={(e) => setForm({ ...form, [`${p}_email`]: e.target.value })} />
                    <input data-testid={`party-${p}_phone`} type="tel" className={inp} placeholder="Phone" value={form[`${p}_phone`]} onChange={(e) => setForm({ ...form, [`${p}_phone`]: e.target.value })} />
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="txn-submit-button" className="w-full py-3 rounded-full bg-[#1F3C88] text-white font-semibold hover:bg-[#152A61] transition-colors duration-200">Create Transaction</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewBucket} onOpenChange={(v) => { if (!v) setViewBucket(null); }}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tasks-popup">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{TASK_BUCKET_LABELS[viewBucket] || "Tasks"}</DialogTitle></DialogHeader>
          {(() => {
            const rows = (viewBucket && dash) ? (dash[viewBucket] || []) : [];
            if (rows.length === 0) return <div className="py-8 text-center text-[#8a8a86]">Nothing here right now.</div>;
            const groups = {};
            for (const it of rows) {
              const key = (it.coordinator_name || "").trim() || "Unassigned";
              (groups[key] = groups[key] || []).push(it);
            }
            const names = Object.keys(groups).sort((a, b) => {
              if (a === "Unassigned") return 1;
              if (b === "Unassigned") return -1;
              return a.localeCompare(b);
            });
            const shown = popupCoord === "__all__" ? names : names.filter((n) => n === popupCoord);
            return (
              <div>
                <div className="mb-4">
                  <select data-testid="popup-coordinator-filter" value={popupCoord} onChange={(e) => setPopupCoord(e.target.value)}
                    className="w-full sm:w-64 px-3 py-2 bg-white border border-[#E5E5E2] rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#F7941D]">
                    <option value="__all__">All Coordinators ({rows.length})</option>
                    {names.map((n) => <option key={n} value={n}>{n} ({groups[n].length})</option>)}
                  </select>
                </div>
                <div className="space-y-4">
                  {shown.map((n) => (
                    <div key={n} data-testid={`popup-group-${n.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                      <div className="flex items-center justify-between px-1 mb-1.5">
                        <div className="text-[11px] tracking-[0.12em] uppercase font-semibold text-[#575755] flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{n}</div>
                        <span className="text-[11px] font-semibold text-[#1F3C88]">{groups[n].length} task{groups[n].length === 1 ? "" : "s"}</span>
                      </div>
                      <div className="border border-[#E5E5E2] rounded-sm divide-y divide-[#F0EFEC]">
                        {groups[n].map((it, i) => (
                          <button key={i} data-testid={`popup-item-${n.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}-${i}`} onClick={() => { setViewBucket(null); navigate(`/transactions/${it.transaction_id}`); }}
                            className="w-full text-left px-4 py-3 flex items-center justify-between gap-4 hover:bg-[#FBFAF8] transition-colors">
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-[#0A0A0A] truncate">{it.title}</div>
                              <div className="text-xs text-[#8a8a86] truncate">{it.property_address}</div>
                            </div>
                            <span className="text-xs font-semibold text-[#575755] shrink-0">{it.due_date}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskTile({ label, sub, items, nav, tone, bucket, index = 2, onViewAll }) {
  const count = items?.length || 0;
  const numTone = tone === "text-[#2E5C47]" ? "text-[#2E5C47]" : tone === "text-[#B07D14]" ? "text-[#B07D14]" : "text-[#992B2B]";
  return (
    <div data-testid={`tile-${bucket}`} className="bg-white border border-[#E5E5E2] rounded-sm p-6 flex flex-col animate-fade-up" style={{ animationDelay: `${index * 60}ms` }}>
      <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#575755]">{label}</div>
      <div className={`mt-3 font-serif text-3xl sm:text-4xl leading-none ${numTone}`}>{count}</div>
      {count === 0 ? (
        <div className="mt-2 text-sm text-[#575755] flex-1">{sub}</div>
      ) : (
        <div className="mt-3 space-y-1.5 max-h-32 overflow-y-auto flex-1">
          {items.slice(0, 6).map((it, i) => (
            <button key={i} data-testid={`tile-${bucket}-${it.transaction_id}-${i}`} onClick={() => nav(`/transactions/${it.transaction_id}`)} className="w-full text-left group py-0.5">
              <div className="text-sm text-[#0A0A0A] truncate group-hover:text-[#1F3C88] transition-colors">{it.title}</div>
              <div className="text-[11px] text-[#a3a099] flex items-center gap-1.5 flex-wrap">
                <span className="truncate">{it.property_address} · {it.due_date}</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#EAF0FB] text-[#1F3C88] font-semibold"><User className="w-3 h-3" />{it.assignee || "Unassigned"}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      <button data-testid={`viewall-${bucket}`} onClick={() => onViewAll?.(bucket)}
        className="mt-3 pt-3 border-t border-[#F0EFEC] inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F3C88] hover:text-[#152A61] transition-colors">
        View all {count} <ExternalLink className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function AddressTile({ label, value, sub, items, nav, index = 0 }) {
  return (
    <div data-testid={`tile-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="bg-white border border-[#E5E5E2] rounded-sm p-6 animate-fade-up" style={{ animationDelay: `${index * 60}ms` }}>
      <div className="text-xs tracking-[0.2em] uppercase font-semibold text-[#575755]">{label}</div>
      <div className="mt-3 font-serif text-3xl sm:text-4xl text-[#1F3C88] leading-none">{value}</div>
      {(!items || items.length === 0) ? (
        <div className="mt-2 text-sm text-[#575755]">{sub}</div>
      ) : (
        <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
          {items.map((it) => {
            const du = it.closing_date ? Math.ceil((new Date(it.closing_date) - new Date()) / 86400000) : null;
            const dc = du == null ? "text-[#a3a099]" : du <= 3 ? "text-[#992B2B] font-semibold" : du <= 7 ? "text-[#B07D14] font-semibold" : "text-[#a3a099]";
            return (
              <button key={it.transaction_id} data-testid={`tile-addr-${it.transaction_id}`} onClick={() => nav(`/transactions/${it.transaction_id}`)}
                className="w-full text-left group flex items-baseline justify-between gap-2 py-0.5 hover:text-[#1F3C88] transition-colors">
                <span className="text-sm text-[#0A0A0A] truncate group-hover:text-[#1F3C88]">{it.property_address || "Untitled"}</span>
                {it.closing_date && <span className={`text-[11px] shrink-0 ${dc}`}>{fmtMDY(it.closing_date)}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TaskBucket({ icon, title, items, tone, nav }) {
  return (
    <div>
      <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wide mb-2 ${tone}`}>{icon}{title} ({items?.length || 0})</div>
      {(!items || items.length === 0) ? <div className="text-xs text-[#a3a099] pl-6">Nothing here.</div> :
        items.slice(0, 8).map((it, i) => (
          <button key={i} onClick={() => nav(`/transactions/${it.transaction_id}`)} className="w-full text-left px-3 py-2 rounded-sm hover:bg-[#FBFAF8] transition-colors duration-200">
            <div className="text-sm text-[#0A0A0A]">{it.title}</div>
            <div className="text-xs text-[#575755]">{it.property_address} · {it.due_date}</div>
          </button>
        ))}
    </div>
  );
}
